import { useMemo } from "react";
import { Download04Icon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import type { Attachment } from "../types";
import { isImageAttachment } from "../utils/attachment";

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Renders a message's attachments: images as a grid of thumbnails, other files
// as downloadable cards. Shared by SMS and chat message content.
export const MessageAttachments = ({
  attachments
}: {
  attachments?: Attachment[];
}) => {
  const imageAttachments = useMemo(
    () =>
      attachments?.filter((att) => att.file_url && isImageAttachment(att)) ??
      [],
    [attachments]
  );
  const fileAttachments = useMemo(
    () =>
      attachments?.filter((att) => att.file_url && !isImageAttachment(att)) ??
      [],
    [attachments]
  );

  return (
    <>
      {imageAttachments.length > 0 ? (
        <div className="flex flex-wrap gap-2 pt-4">
          {imageAttachments.map((att) => (
            <a
              key={att.id}
              href={att.file_url}
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <img
                src={att.file_url}
                alt={att.filename}
                className="max-h-64 max-w-full rounded-lg object-cover"
              />
            </a>
          ))}
        </div>
      ) : null}

      {fileAttachments.length > 0 ? (
        <div className="flex flex-wrap gap-3 pt-4">
          {fileAttachments.map((att) => (
            // Opens the browser's own preview in a new tab. `download` used to
            // force a save on same-origin files and was ignored on S3 URLs,
            // which then navigated the portal tab itself.
            // `min-w-0` lets the card shrink inside a narrow bubble (the pop-up)
            // so a long filename truncates instead of overflowing sideways.
            <a
              key={att.id}
              href={att.file_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex min-w-0 max-w-[min(320px,100%)] items-center gap-3 p-3 rounded-xl border border-alpha-100 hover:bg-background-800 transition-colors"
            >
              <HugeiconsIcon
                icon={Download04Icon}
                size={24}
                className="text-content-300 flex-shrink-0"
              />
              <div className="flex flex-col min-w-0">
                <span className="text-sm font-medium text-content-1000 truncate">
                  {att.filename}
                </span>
                {att.file_size_bytes ? (
                  <span className="text-xs text-content-200">
                    {formatFileSize(att.file_size_bytes)}
                  </span>
                ) : null}
              </div>
            </a>
          ))}
        </div>
      ) : null}
    </>
  );
};
